import { CheckSquare } from "lucide-react";
import { type RaRecord, useGetIdentity, useTranslate } from "ra-core";

import { ReferenceField } from "@/components/admin/reference-field";
import { RelativeDate } from "../misc/RelativeDate";
import { useGetSalesName } from "../sales/useGetSalesName";
import type { ActivityTask, ActivityTaskUpdated } from "../types";
import { useActivityLogContext } from "./ActivityLogContext";

type ActivityLogTaskUpdatedProps = {
  activity: RaRecord & ActivityTaskUpdated;
};

/** How much of a task body the feed shows before it cuts. */
const EXCERPT_LENGTH = 90;

/**
 * A task body can run to several paragraphs -- an account brief, a list of
 * open questions, a block of reference. The feed is a timeline, not a reader,
 * and one such task pushed every other entry off the screen.
 *
 * Tasks here are written headline first, so the first line is the summary and
 * everything after it is detail. The feed shows that first line, cut if it is
 * still long, and the task itself holds the rest.
 */
const excerpt = (text: string) => {
  const [firstLine = ""] = text.split("\n");
  const headline = firstLine.trim();
  return headline.length > EXCERPT_LENGTH
    ? `${headline.slice(0, EXCERPT_LENGTH).trimEnd()}...`
    : headline;
};

/**
 * The task columns worth naming in the feed, in the order they are announced.
 *
 * The `log_task_change` trigger diffs the whole row, so the payload also
 * carries columns a reader never sees (`id` above all). "id changed" is not
 * something anyone can act on, so only this list is announced -- and each entry
 * is named by its form label (`resources.tasks.fields.*`), never by its column
 * name.
 *
 * `done_date` is deliberately absent: finishing or reopening a task is its own
 * event, not a field edit. See `getDoneTransition`.
 */
const ANNOUNCED_FIELDS = [
  "text",
  "type",
  "due_date",
  "contact_id",
  "company_id",
  "deal_id",
  "sales_id",
  "attempt_number",
  "outcome",
] as const;

type DoneTransition = "completed" | "reopened";

/**
 * Reads the done/undone transition out of the trigger's `{ field: { old, new } }`
 * diff: a task that gained a completion date was finished, one that lost it was
 * reopened.
 */
function getDoneTransition(
  changes: ActivityTask["changes"],
): DoneTransition | null {
  const change = changes?.done_date;
  if (change == null || typeof change !== "object" || !("new" in change)) {
    return null;
  }
  return (change as { new: unknown }).new != null ? "completed" : "reopened";
}

function getAnnouncedFields(changes: ActivityTask["changes"]) {
  return ANNOUNCED_FIELDS.filter(
    (field) => changes != null && field in changes,
  );
}

const HEADLINES: Record<DoneTransition | "changed", [string, string]> = {
  completed: ["crm.activity.completed_task", "crm.activity.you_completed_task"],
  reopened: ["crm.activity.reopened_task", "crm.activity.you_reopened_task"],
  changed: ["crm.activity.changed_task", "crm.activity.you_changed_task"],
};

/**
 * One task update in the activity feed: who changed what, on which task, about
 * which contact or account.
 */
export function ActivityLogTaskUpdated({
  activity,
}: ActivityLogTaskUpdatedProps) {
  const context = useActivityLogContext();
  const translate = useTranslate();
  const { task } = activity;
  const { identity, isPending } = useGetIdentity();
  const isCurrentUser = !isPending && identity?.id === activity.sales_id;
  const salesName = useGetSalesName(activity.sales_id ?? undefined, {
    enabled: !isCurrentUser && activity.sales_id != null,
  });

  const doneTransition = getDoneTransition(task.changes);
  // A completion swallows whatever else was saved with it: the event a reader
  // scans for is "this got done", not the fields that moved on the way.
  const changedFields = doneTransition ? [] : getAnnouncedFields(task.changes);

  // Nothing happened that a reader could act on -- an internal column moved, or
  // the diff is empty. A row saying only "a task changed" is noise, so there is
  // no row.
  if (!doneTransition && changedFields.length === 0) {
    return null;
  }

  // A task can outlive its account manager, and a change made outside the app
  // has no actor at all; the trail says so rather than leaving a blank name.
  const name =
    activity.sales_id == null
      ? translate("resources.tasks.history.unknown_user")
      : salesName;
  const [otherHeadline, ownHeadline] = HEADLINES[doneTransition ?? "changed"];

  /*
    A named person is the more actionable target, so it wins when the task has
    one; a task filed only against an account falls back to the account -- and
    on that account's own page even that is left out, since the feed is already
    about it.
  */
  const hasContactTarget = task.contact_id != null;
  const hasCompanyTarget =
    !hasContactTarget && context !== "company" && activity.company_id != null;

  return (
    <div className="p-0">
      <div className="flex flex-row gap-2 items-start w-full">
        <CheckSquare className="w-5 h-5 shrink-0 text-muted-foreground" />
        <span className="text-muted-foreground text-sm flex-grow">
          {translate(isCurrentUser ? ownHeadline : otherHeadline, {
            name,
            fields: changedFields
              .map((field) =>
                translate(`resources.tasks.fields.${field}`, { _: field }),
              )
              .join(", "),
          })}{" "}
          <span className="text-foreground">{excerpt(task.text)}</span>
          {hasContactTarget && (
            <>
              {" "}
              {translate("crm.activity.for_contact")}{" "}
              <ReferenceField
                source="contact_id"
                reference="contacts"
                record={task}
                link="show"
              />
            </>
          )}
          {hasCompanyTarget && (
            <>
              {" "}
              {translate("crm.activity.at_company")}{" "}
              <ReferenceField
                source="company_id"
                reference="companies"
                record={activity}
                link="show"
              />
            </>
          )}
          {context !== "company" && (
            <>
              {" "}
              <RelativeDate date={activity.date} />
            </>
          )}
        </span>
        {context === "company" && (
          <span className="text-muted-foreground text-sm">
            <RelativeDate date={activity.date} />
          </span>
        )}
      </div>
    </div>
  );
}
